import SyncState from './lib/SyncState'
import { addTimestamps } from '../utils'
import isEqual from 'lodash/isEqual'

export const RUN_CONFIG = {
  queue: 'Run_Section_ANU_PBS/mode/batch/batch/queue',
  memory: 'Run_Section_ANU_PBS/mode/batch/batch/vmem',
  memoryUnits: 'Run_Section_ANU_PBS/mode/batch/batch/vmem_units',
  time: 'Run_Section_ANU_PBS/mode/batch/batch/walltime (hh:mm)'
}

export const GLOBAL_CONFIG = {
  layout: 'Top_Level/MPI/num_processors'
}

function mapKeys (mapping, source) {
  return Array.from(Object.entries(mapping)).reduce((o, [sourceKey, destKey]) => {
    return Object.assign(o, {
      [destKey]: source[sourceKey]
    })
  }, {})
}

const Process = SyncState('process', {
  processUpdate (state, { processID, data }) {
    const process = state[processID]
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        ...data,
        id: processID
      })
    }
  },

  applyConfigWizard (state, { processID, config }) {
    const process = state[processID]

    const globalConfig = {
      ...process.globalConfig,
      ...mapKeys(GLOBAL_CONFIG, config)
    }

    const runConfig = {
      ...process.runConfig,
      ...mapKeys(RUN_CONFIG, config)
    }

    if (
      isEqual(runConfig, process.runConfig) &&
      isEqual(config, process.config) &&
      isEqual(globalConfig, process.globalConfig)
    ) return state

    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        config,
        runConfig,
        globalConfig,
        id: processID
      })
    }
  },

  nodeAdd (state, node) {
    const { processID } = node
    const process = state[processID]

    const nodes = process.nodes.slice()

    if (node.prevID) {
      const { prevID } = node
      node = Object.assign({}, node)
      // remove prevID property
      delete node.prevID
      const index = nodes.findIndex(n => n.id === prevID) + 1
      if (index) {
        nodes.splice(index, 0, node)
      } else {
        nodes.push(node)
      }
    } else {
      nodes.push(node)
    }

    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes,
        id: processID
      })
    }
  },

  nodeRemove (state, { processID, nodeID }) {
    const process = state[processID]
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: process.nodes.filter(n => n.id !== nodeID),
        id: processID
      })
    }
  },

  nodeRemoveMany (state, { processID, ids }) {
    ids = new Set(ids || [])
    if (!ids.size) return state
    const process = state[processID]
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: process.nodes.filter(n => !ids.has(n.id)),
        id: processID
      })
    }
  },

  nodeUpdate (state, { processID, nodeID, value, meta }) {
    const process = state[processID]
    if (nodeID === 'globalConfig' || nodeID === 'runConfig') {
      return {
        ...state,
        [processID]: addTimestamps(process, {
          ...process,
          [nodeID]: value
        })
      }
    }
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: process.nodes.map(node => {
          if (node.id !== nodeID) return node
          return {
            ...node,
            ...meta,
            value: value || node.value
          }
        }),
        id: processID
      })
    }
  },

  reorderNodes (state, { processID, order }) {
    const process = state[processID]
    if (!order || !order.length) return state
    const nodes = new Map([...process.nodes.entries()].map(([index, item]) => [item.id, item]))

    const orderIDs = new Set(order)
    if (!Array.from(nodes.keys()).every(key => orderIDs.has(key))) {
      throw new Error('reordering must contain all keys')
    }

    if (orderIDs.size !== order.length) {
      throw new Error('reordering must not contain duplicates')
    }

    if (orderIDs.size !== Array.from(nodes.keys()).length) {
      throw new Error('reordering must contain all keys once')
    }

    if (order[0] !== process.nodes[0].id) {
      throw new Error('reordering cannot reorder root node')
    }

    const newNodes = order.map(nodeID => nodes.get(nodeID))

    let startIndex = order.indexOf(process.startRunNode)
    if (startIndex === -1) startIndex = 0

    let endIndex = order.indexOf(process.endRunNode)
    if (endIndex === -1) endIndex = Infinity
    let { runStartNode, runEndNode } = process
    if (startIndex > endIndex) {
      runStartNode = process.runEndNode
      runEndNode = process.runStartNode
    }

    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: newNodes,
        runStartNode,
        runEndNode,
        id: processID
      })
    }
  },

  resetRun (state, { processID }) {
    const process = state[processID]
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: process.nodes.map(node => ({
          ...node,
          disabled: false
        })),
        runStartNode: undefined,
        runEndNode: undefined,
        id: processID
      })
    }
  },

  nodeMoveUp (state, { processID, nodeID }) {
    const process = state[processID]
    const nodeIndex = process.nodes.findIndex(n => n.id === nodeID)
    // cannot move first node
    if (nodeIndex === 0) return state
    // cannot move node into position 0
    if (nodeIndex === 1) return state
    const node = process.nodes[nodeIndex]
    const newNodes = process.nodes.slice()
    const swapNode = newNodes[nodeIndex - 1]
    newNodes[nodeIndex - 1] = node
    newNodes[nodeIndex] = swapNode
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: newNodes,
        id: processID
      })
    }
  },

  nodeMoveDown (state, { processID, nodeID }) {
    const process = state[processID]
    const nodeIndex = process.nodes.findIndex(n => n.id === nodeID)
    // cannot move first node
    if (nodeIndex === 0) return state
    // cannot move node beyond end
    if (nodeIndex === process.nodes.length - 1) return state
    const node = process.nodes[nodeIndex]
    const newNodes = process.nodes.slice()
    const swapNode = newNodes[nodeIndex + 1]
    newNodes[nodeIndex + 1] = node
    newNodes[nodeIndex] = swapNode
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: newNodes,
        id: processID
      })
    }
  },

  nodeDisableToggle (state, { processID, nodeID }) {
    const process = state[processID]
    const nodeIndex = process.nodes.findIndex(n => n.id === nodeID)
    const newNodes = process.nodes.slice()
    const node = newNodes[nodeIndex]
    newNodes[nodeIndex] = {
      ...node,
      disabled: !node.disabled
    }

    // root node disabled should match first node
    if (nodeIndex === 1) {
      newNodes[0] = {
        ...newNodes[0],
        disabled: !node.disabled
      }
    }

    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        nodes: newNodes,
        id: processID
      })
    }
  },

  setRunStart (state, { processID, nodeID }) {
    const process = state[processID]
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        runStartNode: nodeID,
        id: processID
      })
    }
  },

  setRunEnd (state, { processID, nodeID }) {
    const process = state[processID]
    return {
      ...state,
      [processID]: addTimestamps(process, {
        ...process,
        runEndNode: nodeID,
        id: processID
      })
    }
  }
})

export default Process



// WEBPACK FOOTER //
// ../webmango-core/state/process.js